import { RootCategory } from '@/components/BudgetCalculator/RootCategoryForm';
import { sankeyColors, sankeyColorsAlt } from '@/components/BudgetCalculator/sankeyColors';
import { CustomNodePayload } from '@/components/BudgetCalculator/CustomNode';

/**
 * Returns the base info used to render the sankey diagram
 */
export const getRootCategorySankeyInfo = (root: RootCategory) => {
  // Filter out categories without any number-ish value
  const validCategories = root.categories.filter((category) =>
    category.values.some(
      (values) => values.value && !isNaN(parseFloat(values.value)) && parseFloat(values.value)
    )
  );
  // Compute nodes and values for categories
  const categoryLabels = validCategories.map((category) => category.name);
  const categoryNodes = categoryLabels.map((label) => ({
    name: label,
  }));
  const categoryValues = validCategories.map((category) =>
    category.values
      .map((values) => parseFloat(values.value ?? ''))
      .filter(Boolean)
      .filter((number) => !isNaN(number))
      .reduce((acc, val) => acc + val, 0)
  );
  // Compute nodes and values for category entries
  const categoryEntriesLabel = validCategories.map((category) =>
    category.values
      .filter(
        (values) => values.value && !isNaN(parseFloat(values.value)) && parseFloat(values.value)
      )
      .map((values) => values.name)
  );
  const categoryEntriesNodes = categoryEntriesLabel.map((labels) =>
    labels.map((label) => ({
      name: label,
    }))
  );
  const categoryEntriesValues = validCategories.map((category) =>
    category.values
      .map((values) => parseFloat(values.value ?? ''))
      .filter(Boolean)
      .filter((number) => !isNaN(number))
  );
  // compute total value
  const categoryTotalValue = categoryValues.reduce((acc, val) => acc + val, 0);

  return {
    categoryNodes,
    categoryValues,
    categoryEntriesNodes,
    categoryEntriesValues,
    categoryTotalValue,
  };
};

const getPrecedingCount = (nodes: unknown[][], categoryIndex) =>
  nodes.reduce((acc, val, accIndex) => acc + (accIndex < categoryIndex ? val.length : 0), 0);

export type BaseNode = {
  name: string;
  displayValue?: number;
};

export const getSankeyNodes = (
  incomeEntriesNodes: BaseNode[][],
  investmentsCategoryNodes: BaseNode[],
  investmentsEntriesNodes: BaseNode[][],
  expensesCategoryNodes: BaseNode[],
  expensesEntriesNodes: BaseNode[][],
  budgetValue: number,
  budgetLabel = 'Budget'
) => {
  // The income nodes are simply all the entries
  const incomeNodes = incomeEntriesNodes.flatMap((nodes) =>
    nodes.map((node) => ({ ...node, color: sankeyColors[0], align: 'after' }))
  );

  const lastIncomeNode = incomeNodes[incomeNodes.length - 1] as Pick<
    CustomNodePayload,
    'name' | 'color' | 'poweredBy'
  >;
  if (lastIncomeNode) {
    lastIncomeNode.poweredBy = true;
  }
  // The budget node is a single node
  const budgetNodes = [{ name: budgetLabel, color: sankeyColors[1], displayValue: budgetValue }];

  // The investments nodes are the ones representing each category, and the ones representing each entry in each category
  const investmentsNodes = {
    category: investmentsCategoryNodes.map((category, categoryIndex) => ({
      ...category,
      color: sankeyColors[(1 + categoryIndex + 1) % sankeyColors.length],
    })),
    entries: investmentsEntriesNodes.flatMap((nodes, categoryIndex) =>
      nodes.map((node, index) => ({
        ...node,
        color:
          sankeyColors[
            (1 +
              getPrecedingCount(investmentsEntriesNodes, categoryIndex) +
              2 /* expenses and investments */ +
              index) %
              sankeyColors.length
          ],
      }))
    ),
  };

  // The expenses nodes are the ones representing each category, and the ones representing each entry in each category
  const expensesNodes = {
    category: expensesCategoryNodes.map((category, categoryIndex) => ({
      ...category,
      color:
        sankeyColors[
          (1 + investmentsEntriesNodes.length + categoryIndex + 1) % sankeyColors.length
        ],
    })),
    entries: expensesEntriesNodes.flatMap((nodes, categoryIndex) =>
      nodes.map((node, index) => ({
        ...node,
        color:
          sankeyColors[
            (1 +
              investmentsEntriesNodes.reduce((acc, val) => acc + val.length, 0) +
              getPrecedingCount(expensesEntriesNodes, categoryIndex) +
              2 /* expenses and investments */ +
              index) %
              sankeyColors.length
          ],
      }))
    ),
  };

  return [
    ...incomeNodes,
    ...budgetNodes,
    ...investmentsNodes.category,
    ...investmentsNodes.entries,
    ...expensesNodes.category,
    ...expensesNodes.entries,
  ];
};

export const getSankeyLinks = (
  incomeEntriesValues: number[][],
  investmentsCategoryValues: number[],
  investmentsEntriesValues: number[][],
  expensesCategoryValues: number[],
  expensesEntriesValues: number[][],
  differentiate: boolean
) => {
  const totalIncomeEntries = incomeEntriesValues.reduce((acc, val) => acc + val.length, 0);
  const totalInvestmentsEntries = investmentsEntriesValues.reduce(
    (acc, val) => acc + val.length,
    0
  );

  const incomeLinks = incomeEntriesValues.flatMap((category) =>
    category.map((value, index) => ({
      source: index,
      target: totalIncomeEntries,
      value: value,
      color: sankeyColorsAlt[0],
    }))
  );

  const investmentsCategoryLinks = investmentsCategoryValues.map((value, index) => ({
    source: totalIncomeEntries,
    target: totalIncomeEntries + index + 1,
    value: value,
    color: sankeyColorsAlt[1],
  }));

  const investmentEntriesLinks = investmentsEntriesValues.flatMap((category, categoryIndex) =>
    category.map((value, index) => ({
      source: totalIncomeEntries + categoryIndex + 1,
      target:
        totalIncomeEntries +
        investmentsEntriesValues.length +
        getPrecedingCount(investmentsEntriesValues, categoryIndex) +
        index +
        1,
      value: value,
      color: sankeyColorsAlt[(1 + categoryIndex + 1) % sankeyColorsAlt.length],
    }))
  );

  const expensesCategoryLinks = expensesCategoryValues.map((value, index) => ({
    source: totalIncomeEntries,
    target:
      totalIncomeEntries + investmentsCategoryValues.length + totalInvestmentsEntries + 1 + index,
    value: value,
    color: sankeyColorsAlt[differentiate ? sankeyColors.length - 1 : 1],
  }));

  const expensesEntriesLinks = expensesEntriesValues.flatMap((catgeory, categoryIndex) =>
    catgeory.map((value, index) => ({
      source:
        totalIncomeEntries +
        investmentsCategoryValues.length +
        totalInvestmentsEntries +
        1 +
        categoryIndex,
      target:
        totalIncomeEntries +
        investmentsCategoryValues.length +
        totalInvestmentsEntries +
        expensesEntriesValues.length +
        getPrecedingCount(expensesEntriesValues, categoryIndex) +
        index +
        1,
      value: value,
      color:
        sankeyColorsAlt[
          (1 + investmentsEntriesValues.length + categoryIndex + 1) % sankeyColorsAlt.length
        ],
    }))
  );

  return [
    ...incomeLinks,
    ...investmentsCategoryLinks,
    ...investmentEntriesLinks,
    ...expensesCategoryLinks,
    ...expensesEntriesLinks,
  ];
};

export const getSankeyData = (
  income: RootCategory,
  investments: RootCategory,
  expenses: RootCategory,
  budgetLabel = 'Budget'
) => {
  // Should expenses and investment be differentiated?
  const differentiate = true;

  // Income
  const {
    categoryEntriesNodes: incomeEntriesNodes,
    categoryEntriesValues: incomeEntriesValues,
    categoryTotalValue: incomeTotalValue,
  } = getRootCategorySankeyInfo(income);

  // Investments
  const {
    categoryNodes: investmentsCategoryNodes,
    categoryValues: investmentsCategoryValues,
    categoryEntriesNodes: investmentsEntriesNodes,
    categoryEntriesValues: investmentsEntriesValues,
  } = getRootCategorySankeyInfo(investments);

  // Expenses
  const {
    categoryNodes: expensesCategoryNodes,
    categoryValues: expensesCategoryValues,
    categoryEntriesNodes: expensesEntriesNodes,
    categoryEntriesValues: expensesEntriesValues,
  } = getRootCategorySankeyInfo(expenses);

  const nodes = getSankeyNodes(
    incomeEntriesNodes,
    investmentsCategoryNodes,
    investmentsEntriesNodes,
    expensesCategoryNodes,
    expensesEntriesNodes,
    incomeTotalValue,
    budgetLabel
  );

  const links = getSankeyLinks(
    incomeEntriesValues,
    investmentsCategoryValues,
    investmentsEntriesValues,
    expensesCategoryValues,
    expensesEntriesValues,
    differentiate
  );

  return {
    nodes,
    links,
  };
};
